// Legacy Font Awesome markup shim.
//
// Seven shared packages (@apexl/icarus-branding, -content-flows,
// -notifications, -taxonomy, -permissions, -views, -content-library) still
// write their icons as raw `<i class="fal fa-arrow-up"></i>`. That markup was
// drawn by the Font Awesome icon font, which the icarus-core 3.1 icon
// migration removed: no stylesheet defines `.fal` or `.fa-*` any more, so each
// `<i>` is an empty inline element and an icon-only button is an empty box.
//
// startLegacyFaIcons() is called once, from index.js after the app has
// mounted. It fills every such `<i>` in the document with the matching Cygnus
// sprite glyph, now and whenever a component renders more of them, and gives
// an icon-only button a title and an aria-label when it has neither. Which
// glyph and which label are decided in legacyFaIconNames.mjs.
//
// What it will not touch:
//   - anything that is not an `<i>`;
//   - an `<i>` whose class is not Font Awesome markup (a style token such as
//     `fal` plus an `fa-NAME` token), e.g. an `<i>` used for italics;
//   - an `<i>` that has content it did not put there;
//   - a title or aria-label that somebody else wrote.
//
// One observer watches the whole document, not one per component, because
// the packages also render into teleported modals outside the app root.
import cygnusSpriteHtml from '@root/services/icons/cygnusSpriteHtml';
import { parseFaClass, cygnusIconFor, labelFor, mayLabel } from './legacyFaIconNames.mjs';

// What the shim drew into an `<i>`: "name|variant|spin". Its presence is how
// a filled element is recognised, and a change of class is detected.
const FILLED = 'data-legacy-fa';
// Set on a button whose title and aria-label the shim wrote.
const LABELLED = 'data-legacy-fa-label';

// Cheap pre-filter: every Font Awesome icon has an `fa-NAME` class token.
const CANDIDATES = 'i[class*="fa-"]';
const BUTTONS = 'button, a, [role="button"]';
const DANGER = ['text-danger', 'btn-danger', 'btn-outline-danger'];

/** The shim's own glyph is the element's only content. */
function holdsOnlyOurGlyph(el) {
  return el.childNodes.length === 1
    && el.firstChild.nodeType === 1
    && el.firstChild.classList.contains('cygnus-icon');
}

function label(el, faName) {
  const button = el.closest(BUTTONS);
  if (!button) return;
  const ours = button.hasAttribute(LABELLED);
  const allowed = mayLabel({
    text: button.textContent,
    title: button.getAttribute('title'),
    ariaLabel: button.getAttribute('aria-label'),
    ariaLabelledBy: button.getAttribute('aria-labelledby'),
    ours,
  });
  const text = allowed
    ? labelFor(faName, { danger: DANGER.some((c) => button.classList.contains(c)) })
    : null;
  if (text) {
    if (button.getAttribute('title') !== text) button.setAttribute('title', text);
    if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
    if (!ours) button.setAttribute(LABELLED, '1');
  } else if (ours) {
    // The shim's label no longer applies (the icon changed, or the button
    // now has text): take back what it wrote, and only that.
    button.removeAttribute('title');
    button.removeAttribute('aria-label');
    button.removeAttribute(LABELLED);
  }
}

/**
 * Fill one `<i>` if it is empty Font Awesome markup, refresh it if the shim
 * filled it earlier and its class has changed since, and otherwise leave it.
 *
 * @param {Element} el
 * @returns {boolean} whether the element was changed
 */
export function fillLegacyFaIcon(el) {
  if (!el || el.nodeName !== 'I') return false;
  const was = el.getAttribute(FILLED);
  if (was === null && el.childNodes.length > 0) return false; // somebody else's content

  const fa = parseFaClass(el.getAttribute('class') || '');
  const icon = fa ? cygnusIconFor(fa.name) : null;
  const key = icon ? `${fa.name}|${fa.variant}|${fa.spin ? 1 : 0}` : null;
  // Nothing to do when it already shows the right glyph (or is rightly empty).
  if (key === was && (key === null || el.childNodes.length > 0)) return false;

  const mine = was !== null && (el.childNodes.length === 0 || holdsOnlyOurGlyph(el));
  if (was !== null && !mine) {
    // Filled earlier, but the content has been replaced since: not ours now.
    el.removeAttribute(FILLED);
    return true;
  }
  if (key === null) {
    if (was === null) return false;
    el.innerHTML = '';
    el.removeAttribute(FILLED);
    return true;
  }
  el.setAttribute(FILLED, key);
  el.innerHTML = cygnusSpriteHtml(icon, { variant: fa.variant, spin: fa.spin });
  label(el, fa.name);
  return true;
}

/** Fill every candidate at or under `node`. */
function sweep(node) {
  if (!node || node.nodeType !== 1) return;
  if (node.nodeName === 'I') {
    fillLegacyFaIcon(node);
    return;
  }
  if (!node.firstElementChild) return;
  const found = node.querySelectorAll(CANDIDATES);
  for (let i = 0; i < found.length; i += 1) fillLegacyFaIcon(found[i]);
}

let stop = null;

/**
 * Start the shim: one sweep of the document, then one MutationObserver.
 *
 * The observer's callback is already a batch (the browser delivers every
 * mutation of a turn in one call, before the next paint, so a filled icon
 * never flashes empty). It looks at the nodes that were added and at `<i>`
 * elements whose class changed, and skips what the shim itself wrote.
 *
 * @param {Element} [root=document.body]
 * @returns {Function} stops the observer
 */
export default function startLegacyFaIcons(root) {
  if (stop) return stop;
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
  const target = root || document.body;
  if (!target) return () => {};

  sweep(target);

  const observer = new MutationObserver((records) => {
    const seen = new Set();
    for (let r = 0; r < records.length; r += 1) {
      const record = records[r];
      if (record.type === 'attributes') {
        if (record.target.nodeName === 'I') seen.add(record.target);
      } else if (record.target.nodeName === 'I' && record.target.hasAttribute(FILLED)) {
        // A childList record on a filled `<i>` is the shim's own glyph going
        // in. Only an `<i>` that has been emptied again needs another look.
        if (record.target.childNodes.length === 0) seen.add(record.target);
      } else {
        for (let n = 0; n < record.addedNodes.length; n += 1) {
          if (record.addedNodes[n].nodeType === 1) seen.add(record.addedNodes[n]);
        }
      }
    }
    seen.forEach(sweep);
  });
  observer.observe(target, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  stop = () => {
    observer.disconnect();
    stop = null;
  };
  return stop;
}
